// @ts-nocheck
import React, { memo, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DateFormats } from "app/constants/dateConst";
import styles from "./GroupedTestRecordsTable.module.scss";
import { hasPermission } from "app/components/Permission/Permission";
import { PermissionEnum } from "app/constants/permissionConst";
import Table, { Column } from "app/components/Table/Table";
import { TableProps, PaginationConfig } from "antd/es/table";
import qs from "query-string";
import { getOrderBy } from "app/helpers/apiHelper";
import { OrderByDef } from "app/types/apiType";
import _camelCase from "lodash/camelCase";
import _upperFirst from "lodash/upperFirst";
import cx from "classnames";
import {
  GroupedTestRecordDTO,
  GroupedTestRecordTestRecordDTO,
  MeasurementMethod,
} from "@generated/v2";
import { AnnotationActionsEnum } from "app/constants/annotationConst";
import TestRecordsTable from "./components/TestRecordsTable/TestRecordsTable";
import { getTestResultTranslationKey } from "app/helpers/utilHelper";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { ReactComponent as IconComment } from "app/assets/ch-comment.svg";
import { ReactComponent as IconFile } from "app/assets/ch-file.svg";
import { Tag } from "antd";
import { columnConfig } from "app/constants/testRecordsConst";
import { useTestRecords } from "app/state/testRecords";
import { cloneDeep } from "lodash";

const isGroupExpandable = (group: GroupedTestRecordDTO) =>
  group?.testRecords?.length != null && group?.testRecords?.length > 0;

const isGroupSingle = (group: GroupedTestRecordDTO) =>
  group.testRecords?.length === 1 && group.testRecords?.[0] != null;

const hasAttachment = (group: GroupedTestRecordDTO) =>
  group.testRecords?.some(
    record => !!record.attachments && record.attachments?.length > 0
  );

const hasComment = (group: GroupedTestRecordDTO) =>
  group.testRecords?.some(record => !!record.comments);

interface IProps extends TableProps<GroupedTestRecordDTO> {
  redirectTo: (id?: number, path?: string) => void;
  orderBy?: OrderByDef;
  onUpdateRecord: () => void;
}

const GroupedTestRecordsTable = ({
  redirectTo,
  orderBy,
  onUpdateRecord,
  ...rest
}: IProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const [enabledColumnNames, setEnabledColumnNames] = useState([]);
  const { views } = useTestRecords();

  const currentSearch = qs.parse(location.search);
  const viewId = Number(currentSearch?.viewId);

  const handleTableChange = (pagination: PaginationConfig, filters, sorter) => {
    // Keep the old search params
    const currentSearch = qs.parse(location.search);

    // and overwrite or add new values
    const values = {
      ...currentSearch,
      page: pagination.current,
      orderBy:
        (sorter.order && getOrderBy(sorter.columnKey, sorter.order)) ||
        undefined,
    };

    history.push({
      pathname: location.pathname,
      search: qs.stringify(values, { arrayFormat: "comma" }),
    });
  };

  // Filter enabled columns and sort by order
  const handleColumns = columns => {
    const enabledColumnNames = columns
      .filter(col => col.enabled)
      .sort((a, b) => a.order - b.order)
      .map(col => col.label);
    setEnabledColumnNames(enabledColumnNames);
  };

  useEffect(() => {
    if (viewId) {
      const view = views?.views?.find(view => view.id === viewId);
      const columns = cloneDeep(view?.columns);
      if (columns && Array.isArray(columns)) {
        handleColumns(columns);
      }
    } else {
      handleColumns(columnConfig);
    }
  }, [viewId, views]);

  // Function to render columns based on configuration
  const renderColumn = (columnName: string) => {
    switch (columnName) {
      case "measurementMethod":
        return (
          <Column
            title={t("testRecords.measurement")}
            key="measurementMethod"
            dataIndex="measurementMethod"
            width={150}
            render={(text, row: GroupedTestRecordDTO) => (
              <Tag
                className={cx(
                  styles.tag,
                  MeasurementMethod.Quantitative ===
                    row.testRecords?.[0]?.testType.measurementMethod
                    ? styles.quantitativeTag
                    : styles.qualitativeTag
                )}
              >
                {t(
                  `measurementMethod.${_camelCase(
                    row.testRecords?.[0]?.testType.measurementMethod
                  )}`
                )}
              </Tag>
            )}
          />
        );
      case "annotation":
        return (
          <Column
            title={t("testRecords.columnAnnotations")}
            key="annotation"
            dataIndex="annotation"
            width={170}
            render={text =>
              text ? t(`annotations.type${_upperFirst(text)}`) : ""
            }
          />
        );
      case "rawQR":
        return (
          <Column
            title={t("testRecords.columnQR")}
            key="rawQR"
            dataIndex="rawQR"
            width={250}
            render={(text, row: GroupedTestRecordDTO) =>
              isGroupSingle(row) && row.testRecords?.[0].rawQR
            }
          />
        );
      case "batchNumber":
        return (
          <Column
            title={t("testRecords.batchNumber")}
            key="batchNumber"
            dataIndex="batchNumber"
            width={170}
          />
        );
      case "concentration":
        return (
          <Column
            title={t("testRecords.concentration")}
            key="concentration"
            dataIndex="concentration"
            width={170}
          />
        );
      case "route":
        return (
          <Column
            title={t("testRecords.columnRoute")}
            key="route"
            dataIndex="route"
            width={150}
            sorter
            sortOrder={
              (orderBy?.key === "route" && orderBy?.direction) || undefined
            }
          />
        );
      case "deviceType":
        return (
          <Column
            title={t("testRecords.deviceType")}
            key="deviceType"
            dataIndex="deviceType"
            width={150}
            sorter
            sortOrder={
              (orderBy?.key === "deviceType" && orderBy?.direction) || undefined
            }
            render={(text, row: GroupedTestRecordDTO) =>
              row?.testRecords?.[0]?.readerType || t("testRecords.unknown")
            }
          />
        );
      case "siteId":
        return hasPermission([PermissionEnum.ACCOUNTS_CUSTOMER_READ]) ? (
          <Column
            title={t("testRecords.columnSite")}
            key="siteId"
            dataIndex="site.name"
            width={125}
            sorter
            sortOrder={
              (orderBy?.key === "siteId" && orderBy?.direction) || undefined
            }
          />
        ) : null;
      case "username":
        return (
          <Column
            title={t("testRecords.columnUser")}
            key="user.username"
            dataIndex="user.username"
            width={170}
            sorter
            sortOrder={
              (orderBy?.key === "user.username" && orderBy?.direction) ||
              undefined
            }
          />
        );
      case "operatorId":
        return (
          <Column
            title={t("testRecords.columnOperator")}
            key="operatorId"
            dataIndex="operatorId"
            width={170}
            sorter
            sortOrder={
              (orderBy?.key === "operatorId" && orderBy?.direction) || undefined
            }
          />
        );
      case "attachments":
        return (
          <Column
            key="attachments"
            width={30}
            render={(text, row: GroupedTestRecordDTO) => {
              return hasAttachment(row) && <IconFile />;
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ErrorFallbackUI>
      <Table
        className={styles.table}
        rowClassName={row =>
          cx(styles.row, {
            [styles.isDeleted]:
              // @ts-ignore
              row.annotation === AnnotationActionsEnum.DELETED,
          })
        }
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
        expandRowByClick
        expandedRowRender={(row: GroupedTestRecordDTO) => {
          if (isGroupExpandable(row)) {
            return (
              <TestRecordsTable
                dataSource={row.testRecords!}
                redirectTo={redirectTo}
                onUpdateRecord={onUpdateRecord}
              />
            );
          }
        }}
        {...rest}
      >
        <Column
          title={t("testRecords.columnFlowID")}
          key="id"
          dataIndex="id"
          width={150}
          sorter
          sortOrder={(orderBy?.key === "id" && orderBy.direction) || undefined}
        />
        <Column
          title={t("testRecords.columnDateTime")}
          key="testDate"
          dataIndex="testDate"
          width={150}
          sorter
          sortOrder={
            (orderBy?.key === "testDate" && orderBy.direction) || undefined
          }
          render={text => {
            return moment.parseZone(text).format(DateFormats.DATE_TIME);
          }}
        />
        <Column
          title={t("testRecords.columnTest")}
          key="testTypeId"
          dataIndex="testType.name"
          width={250}
          sorter
          sortOrder={
            (orderBy?.key === "testTypeId" && orderBy?.direction) || undefined
          }
          render={(text, row: GroupedTestRecordDTO) =>
            row?.testRecords?.[0].testType?.name
          }
        />
        <Column
          title={t("testRecords.columnTestResult")}
          key="result"
          dataIndex="result"
          width={135}
          render={(text, render: GroupedTestRecordTestRecordDTO) =>
            t(
              `testResultEnum.${_camelCase(
                getTestResultTranslationKey(text, render?.annotation)
              )}`
            )
          }
        />
        {/* Render enabled columns in order */}
        {enabledColumnNames
          .map(columnName => renderColumn(columnName))
          .filter(Boolean)}

        {/* Static columns that are always rendered */}
        <Column
          key="comments"
          width={30}
          render={(text, row: GroupedTestRecordDTO) => {
            return hasComment(row) && <IconComment />;
          }}
        />
        <Column key="actions" width={65} />
      </Table>
    </ErrorFallbackUI>
  );
};

export default memo(GroupedTestRecordsTable);
